import type { CmmdForgeTaskDetail, CmmdProject, CmmdTask } from "@cmmd-forge/contracts";
import { useQuery } from "@tanstack/react-query";

import type { CmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { createCmmdApiClient } from "~/environments/cmmdApi";
import { buildTaskContextModel, type TaskContextModel } from "./TaskContextSurface";

const api = createCmmdApiClient();

interface CanonicalTaskHydration {
  readonly detail: CmmdForgeTaskDetail;
  readonly project: CmmdProject | null;
}

export function useCanonicalTaskContextModel(taskId: string | null): TaskContextModel | null {
  const snapshot = useCmmdWorkIntakeSnapshot();
  const snapshotTask =
    taskId === null ? null : (snapshot.tasks.find((task) => task.id === taskId) ?? null);
  const numericTaskId = parsePositiveTaskId(taskId);
  const hydrationQuery = useQuery({
    queryKey: ["cmmd", "canonical-task-context", numericTaskId],
    queryFn: () => loadCanonicalTaskHydration(numericTaskId as number),
    enabled: numericTaskId !== null && snapshotTask === null,
    staleTime: 30_000,
  });

  return buildCanonicalTaskContextModel({
    taskId,
    snapshot,
    hydration: hydrationQuery.data ?? null,
    hydrationFailed: hydrationQuery.isError,
  });
}

export function buildCanonicalTaskContextModel(input: {
  readonly taskId: string | null;
  readonly snapshot: CmmdWorkIntakeSnapshot;
  readonly hydration: CanonicalTaskHydration | null;
  readonly hydrationFailed: boolean;
}): TaskContextModel | null {
  if (input.taskId === null) return null;
  const snapshotTask = findSnapshotTask(input.snapshot, input.taskId);
  const hydratedTask = resolveHydratedTask(input.hydration);
  const task = snapshotTask ?? hydratedTask;
  const project = resolveTaskProject(input.snapshot, task, input.hydration);
  const campaignName = resolveTaskCampaignName(input.snapshot, task, project);

  return buildTaskContextModel({
    taskId: input.taskId,
    task,
    projectName: project?.title ?? null,
    projectIcon: project?.icon ?? null,
    projectIconUrl: project?.iconUrl ?? null,
    campaignName,
    workspaceName: project?.workspaceName ?? input.snapshot.workspace?.name ?? null,
    sourceUrl: buildTaskSourceUrl(input.snapshot.source, input.taskId),
    syncState: task ? "synced" : input.hydrationFailed ? "failed" : "pending",
  });
}

function findSnapshotTask(snapshot: CmmdWorkIntakeSnapshot, taskId: string): CmmdTask | null {
  return snapshot.tasks.find((candidate) => candidate.id === taskId) ?? null;
}

function resolveHydratedTask(hydration: CanonicalTaskHydration | null): CmmdTask | null {
  return hydration ? taskDetailToContextTask(hydration.detail, hydration.project) : null;
}

function resolveTaskProject(
  snapshot: CmmdWorkIntakeSnapshot,
  task: CmmdTask | null,
  hydration: CanonicalTaskHydration | null,
): CmmdProject | null {
  const snapshotProject = task?.projectId
    ? (snapshot.projects.find((candidate) => candidate.id === task.projectId) ?? null)
    : null;
  return snapshotProject ?? hydration?.project ?? null;
}

function resolveTaskCampaignName(
  snapshot: CmmdWorkIntakeSnapshot,
  task: CmmdTask | null,
  project: CmmdProject | null,
): string | null {
  const snapshotCampaignName = task?.campaignId
    ? (snapshot.campaigns.find((candidate) => candidate.id === task.campaignId)?.title ?? null)
    : null;
  return snapshotCampaignName ?? project?.campaignTitle ?? null;
}

function buildTaskSourceUrl(source: string | null, taskId: string): string | null {
  const normalizedSource = source?.replace(/\/+$/, "") ?? null;
  return normalizedSource ? `${normalizedSource}/tasks/${encodeURIComponent(taskId)}` : null;
}

async function loadCanonicalTaskHydration(taskId: number): Promise<CanonicalTaskHydration> {
  const detail = await api.getForgeTaskDetail(taskId);
  const project =
    detail.projectId === null
      ? null
      : await api.getProject(String(detail.projectId)).catch(() => null);
  return { detail, project };
}

function parsePositiveTaskId(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function taskDetailToContextTask(
  detail: CmmdForgeTaskDetail,
  project: CmmdProject | null,
): CmmdTask {
  return {
    id: String(detail.id),
    title: detail.title,
    status: detail.status,
    priority: detail.priority,
    projectId: detail.projectId === null ? null : String(detail.projectId),
    campaignId: project?.campaignId ?? null,
    workspaceId: project?.workspaceId ?? null,
    workspaceName: project?.workspaceName ?? null,
    dueDate: detail.dueDate,
    updatedAt: detail.updatedAt,
    description: detail.description,
    assigneeId: detail.assignee === null ? null : String(detail.assignee),
    dependsOn: [],
    archivedAt: detail.archivedAt,
  };
}
